import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { cambiarClave } from '../api';
import { Aviso, Boton, Error_ } from '../componentes/basicos';

/**
 * Cambiar la contraseña inicial.
 *
 * No es un aviso que se pueda cerrar: mientras la clave siga siendo la que
 * puso el administrador, el backend rechaza todo lo demás (S-18). La pantalla
 * solo cuenta lo que ya está decidido en el servidor — esconder botones nunca
 * ha sido un control de seguridad.
 */
export function CambiarClave({
  obligado,
  alCambiar,
}: {
  obligado: boolean;
  alCambiar: () => void;
}): ReactNode {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutacion = useMutation({
    mutationFn: () => cambiarClave(actual, nueva),
    onSuccess: alCambiar,
    onError: (e: Error) => setError(e.message),
  });

  function enviar(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);

    if (nueva !== repetida) {
      setError('Las dos contraseñas nuevas no son iguales');
      return;
    }
    if (nueva.length < 12) {
      setError('La contraseña nueva tiene que tener al menos 12 caracteres');
      return;
    }
    if (nueva === actual) {
      setError('La contraseña nueva tiene que ser distinta de la actual');
      return;
    }
    mutacion.mutate();
  }

  return (
    <div className="mx-auto mt-6 max-w-md rounded-2xl border border-borde bg-superficie p-6 shadow-suave">
      <h1 className="mb-1 text-2xl font-semibold">Cambia tu contraseña</h1>

      {obligado ? (
        <Aviso>
          Tu contraseña la puso otra persona, así que todavía no es tuya. Cámbiala para empezar a
          usar el sistema.
        </Aviso>
      ) : (
        <p className="mb-4 text-sm text-tenue">
          Hace falta la contraseña actual aunque tengas la sesión abierta.
        </p>
      )}

      <form onSubmit={enviar} className="mt-4 space-y-3">
        <Campo
          etiqueta="Contraseña actual"
          valor={actual}
          alCambiar={setActual}
          autoComplete="current-password"
        />
        <Campo
          etiqueta="Contraseña nueva"
          valor={nueva}
          alCambiar={setNueva}
          autoComplete="new-password"
          ayuda="Mínimo 12 caracteres. Una frase que recuerdes vale más que ocho símbolos raros."
        />
        <Campo
          etiqueta="Repite la nueva"
          valor={repetida}
          alCambiar={setRepetida}
          autoComplete="new-password"
        />

        {error !== null && <Error_>{error}</Error_>}

        <Boton submit deshabilitado={mutacion.isPending}>
          {mutacion.isPending ? 'Cambiando…' : 'Cambiar contraseña'}
        </Boton>
      </form>
    </div>
  );
}

function Campo({
  etiqueta,
  valor,
  alCambiar,
  autoComplete,
  ayuda,
}: {
  etiqueta: string;
  valor: string;
  alCambiar: (v: string) => void;
  autoComplete: string;
  ayuda?: string;
}): ReactNode {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{etiqueta}</span>
      <input
        type="password"
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        autoComplete={autoComplete}
        required
        className="w-full rounded-md border border-borde bg-superficie px-3 py-2 text-sm"
      />
      {ayuda !== undefined && <span className="mt-1 block text-xs text-tenue">{ayuda}</span>}
    </label>
  );
}
